import { Box } from '@material-ui/core';
import { ClassNameMap } from '@material-ui/styles';
import { CreditCardCriteria, formatDateTimeReadable } from '@nemuru-eng/js-api-client';
import { useEffect } from 'react';
import { generatePath } from 'react-router-dom';

import { CreditCardStatusTag } from '@/components/modules/CreditCard/Detail/Overview/components/CreditCardStatusTag';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { Font } from '@/components/shared/components/Font';
import { SizedIconWithCircle } from '@/components/shared/components/SizedIconWithCircle';
import { LinkToDetailButton } from '@/components/shared/components/SuperSearchBar/components/SearchResultCard/components/shared/LinkToDetailButton';
import { SearchBarFilterOptions } from '@/components/shared/components/SuperSearchBar/types/SearchBarFilterOptions';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import { ICON_NAMES } from '@/config/config';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { getFullNameString } from '@/utils/getFullName';

interface CreditCardSearchResultCardProps {
  classes: ClassNameMap<'root'>;
  selected: boolean;
  searchResult: CreditCardCriteria;
  toggleDialog: () => void;
  searchBarState: SearchBarFilterOptions;
  onNavigate?: boolean;
}

export const CreditCardSearchResultCard = ({
  classes,
  selected,
  searchResult,
  toggleDialog,
  searchBarState,
  onNavigate,
}: CreditCardSearchResultCardProps) => {
  const { palette, customBorderRadius } = useCustomTheme();

  const displayValues = {
    borrowerFullName: getFullNameString(searchResult.customer.fullName) || 'PENDING_INFORMATION',
    status: searchResult.status,
    iconName: ICON_NAMES.CREDIT_CARD,
  };

  const onClick = () => {
    window.location.replace(
      generatePath(DYNAMIC_PAGE_PATHS.CREDIT_CARD, {
        creditCardId: searchResult.id,
      }),
    );
    toggleDialog();
  };

  useEffect(() => {
    if (!selected || !onNavigate) return;
    onClick();
  }, [onNavigate]);

  if (!displayValues) {
    return null;
  }

  return (
    <Box className={'appear-anim'}>
      <CustomPaper
        fill={selected ? 'grey.dark' : 'grey.light'}
        style={{ padding: 8, cursor: 'pointer' }}
        className={classes.root}
        onClick={onClick}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box className={'overflow-text-ellipsis'} display={'flex'} flexDirection={'row'} alignItems={'center'}>
            <SizedIconWithCircle
              copyToClipboard={{
                value: searchResult.id,
                textId: 'COPY_ID',
              }}
              variant={'medium'}
              iconColor={palette.secondary.main}
              backgroundColor={'transparent'}
              customBorderRadius={customBorderRadius.button}
              iconName={displayValues?.iconName}
            />
            <Box display={'flex'} flexDirection={'column'} pl={1} className={'overflow-text-ellipsis'}>
              <Font variant={'body2'} color={'title'} className={'overflow-text-ellipsis'}>
                <strong>{displayValues.borrowerFullName}</strong>
              </Font>
              <Font
                variant={'caption'}
                color={'textSecondary'}
                className={'overflow-text-ellipsis'}
              >{`${formatDateTimeReadable(searchResult[searchBarState.dateToShow])} | ${
                searchResult.businessName
              }`}</Font>
            </Box>
          </Box>
          <Box display={'flex'} flexDirection={'row'} alignItems={'center'} ml={2}>
            <CreditCardStatusTag status={displayValues.status} />
            <LinkToDetailButton onClick={onClick} />
          </Box>
        </Box>
      </CustomPaper>
    </Box>
  );
};
